import { useEffect, useState, useRef } from 'react';
import { View, Pressable, Platform, Animated, StyleSheet } from 'react-native';
import { Text } from 'tamagui';
import * as WebBrowser from "expo-web-browser";
import * as Google from "expo-auth-session/providers/google";
import { HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_TC_MODAL, SUCCESS } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { createURL } from 'expo-linking';
import { GoogleIcon } from '@assets/--native';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import DeviceInfo from 'react-native-device-info';
import styles from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { useNavigation } from '@react-navigation/native';
import { UserType } from './types/UserType';
import { LinearGradient } from 'expo-linear-gradient';
import {
  handleError,
  handleOAuthResponse,
  createOAuthUser,
  fetchUserInfoFromProvider,
  OAuthUser
} from './utils/authUtils';
import { getAuthSessionGuard } from '@utils/authSessionGuard';
import { useTranslation } from 'react-i18next';
import {
  dismissOAuthPopupFromOpener,
  listenForOAuthPopupResult,
  subscribeOAuthCallback,
} from '@utils/oauthWebPopupCompletion';

try { WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true }); } catch (e) { console.warn("maybeCompleteAuthSession error:", e); }

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();


const GoogleButton = ({ code, productId }: {
  code?: string;
  productId?: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //Google Authentication Vars
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const [token, setToken] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const navigation = useNavigation();
  const notificationToken: string = useSelector(
    (state: DefaultRootState) => state.app.notificationToken
  );
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const buttonColors = {
    background: theme ? '#ffffff' : 'rgba(30, 41, 59, 0.95)',
    text: theme ? '#111827' : '#e2e8f0',
    border: theme ? '#e5e7eb' : 'rgba(148, 163, 184, 0.35)',
    hover: theme ? 'rgba(90,192,234,0.25)' : 'rgba(59,130,246,0.2)',
    shadow: theme ? '#000000' : '#020617',
  };

  const GOOGLE_IOS_CLIENT_ID = "530616870665-sn6qm81jg6eh4pr6dm2ov2n6guovrgna.apps.googleusercontent.com";
  const GOOGLE_EXPO_WEB_CLIENT_ID = "530616870665-5e22kgtr0e146vutn8veh5shoqnkis5j.apps.googleusercontent.com";
  const GOOGLE_REDIRECT_URI = createURL('');

  const [request, response, promptAsync] = Google.useAuthRequest({
    androidClientId: "530616870665-mt8uulmtk3r41m5ga22sfuc66j632gik.apps.googleusercontent.com",
    iosClientId: GOOGLE_IOS_CLIENT_ID,
    expoClientId: GOOGLE_EXPO_WEB_CLIENT_ID,
    webClientId: GOOGLE_EXPO_WEB_CLIENT_ID,
    redirectUri: GOOGLE_REDIRECT_URI,
    scopes: ['email', 'profile']
  });

  const handledResponseRef = useRef(false);

  const completeGoogleOAuth = async (accessToken: string) => {
    if (!accessToken || handledResponseRef.current) return;
    handledResponseRef.current = true;
    if (Platform.OS === 'web') {
      dismissOAuthPopupFromOpener();
    }
    setToken(accessToken);
    await getUserInfo(accessToken);
  };

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 1000,
      useNativeDriver: true,
    }).start();
  }, []);

  useEffect(() => {
    if (response?.type === "success" && response.authentication && !handledResponseRef.current) {
      completeGoogleOAuth(response.authentication.accessToken);
    }
  }, [response]);

  // Dev same-tab return reloads the page, so the new AuthRequest has a different
  // state and the popup recovery rejects the callback. Match the state we stored.
  useEffect(() => {
    if (!__DEV__ || Platform.OS !== 'web' || typeof window === 'undefined') return;
    const pending = window.localStorage.getItem('SmartSaaS_oauth_callback_url');
    const expected = window.sessionStorage.getItem('SmartSaaS_oauth_dev_state');
    if (!pending || !expected) return;
    const params = new URLSearchParams((pending.split('#')[1] || ''));
    if (params.get('state') !== expected) return;
    const accessToken = params.get('access_token');
    if (!accessToken) return;
    window.localStorage.removeItem('SmartSaaS_oauth_callback_url');
    window.sessionStorage.removeItem('SmartSaaS_oauth_dev_state');
    void completeGoogleOAuth(accessToken);
  }, []);

  // Recover OAuth when popup wrote callback URL but Expo returned dismiss (COOP race).
  useEffect(() => {
    if (Platform.OS !== 'web' || !request) return undefined;
    return subscribeOAuthCallback(request, (result) => {
      const accessToken =
        result.authentication?.accessToken ?? result.params?.access_token;
      if (accessToken) {
        void completeGoogleOAuth(accessToken);
      }
    });
  }, [request]);

  const handleClick = async () => {
    try {
      // Check if another auth session is active
      if (authGuard.isSessionActive()) {
        const activeSession = authGuard.getActiveSession();
        console.warn(`Cannot start Google auth - ${activeSession} is already active`);
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: `Please wait for ${activeSession} authentication to complete`,
            type: 'error'
          }
        });
        return;
      }

      handledResponseRef.current = false; // Reset before starting new session

      // Dev only. Embedded browsers (the agent browser) drop the Google popup
      // redirect. Production keeps the popup so the signed-in tab stays put.
      const authUrl = (request as { url?: string } | null)?.url;
      if (__DEV__ && Platform.OS === 'web' && authUrl && typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem('ExpoWebBrowserRedirectHandle');
          window.localStorage.removeItem('SmartSaaS_oauth_callback_url');
          const state = (request as { state?: string } | null)?.state;
          if (state) window.sessionStorage.setItem('SmartSaaS_oauth_dev_state', state);
        } catch (_) {}
        window.location.assign(authUrl);
        return;
      }

      // Start auth session with guard — popup must receive redirect and notify this window (not navigate here).
      const res = await authGuard.startSession('Google', async () => {
        const runPrompt = () =>
          promptAsync({
            windowName: 'smartsaas-google-oauth',
            windowFeatures: { width: 515, height: 680 },
          });
        if (Platform.OS === 'web') {
          return listenForOAuthPopupResult(request, runPrompt);
        }
        return runPrompt();
      });

      if (res?.type === 'success') {
        const accessToken =
          res.authentication?.accessToken ?? res.params?.access_token;
        if (accessToken) {
          await completeGoogleOAuth(accessToken);
        }
      } else if (res?.type === 'cancel' || res?.type === 'dismiss') {
        return;
      } else if (res?.type === 'error') {
        console.error('Google auth error:', res.error);
        setTimeout(() => handleError(dispatch, 'Login failed, please try again. If the issue persists, contact support.'), 1000);
      }

      return res;
    } catch (error: any) {
      console.error('Google auth threw an error:', error);
      if (error?.name === 'QuotaExceededError' || error?.message?.includes('Quota')) {
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: 'Your browser storage is full. We have attempted to clear it. Please try logging in again. If it fails, please clear your browser cache.',
            type: 'error'
          }
        });
      } else {
        setTimeout(() => handleError(dispatch, 'An unexpected error occurred during login. Please try again.'), 1000);
      }
    }
  }


  async function getUserInfo(token) {
    if (!token) {
      handleError(dispatch, 'Failed to authenticate Google Account, please try again');
      return
    }
    try {
      dispatch({ type: SHOW_LOADING_MODAL });

      // Fetch user info from Google API
      const user = await fetchUserInfoFromProvider(
        "https://www.googleapis.com/userinfo/v2/me",
        token,
        setUserInfo,
        'Google'
      );

      const newOAuthUser: OAuthUser = createOAuthUser(user, token, 'google', notificationToken);

      // Handle OAuth flow using shared utility
      const res = await apis.handleOAuth(newOAuthUser);
      await handleOAuthResponse(res, dispatch, navigation, notificationToken, 'Google');

    } catch (e) {
      handleError(dispatch, 'Google login failed, please try again. If the issue persists, contact support.');
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
    }
  };

  return (
    <Pressable
      onPress={handleClick}
      style={{
        minHeight: 50,
        height: 50,
        width: '100%',
        maxWidth: Platform.OS === 'web' ? 400 : undefined,
        alignSelf: 'center',
        flexGrow: 0,
        flexShrink: 0,
        elevation: 1,
        shadowRadius: 10,
        shadowOpacity: .2,
        backgroundColor: buttonColors.background,
        borderRadius: 30,
        borderWidth: 1,
        borderColor: buttonColors.border,
        shadowColor: buttonColors.shadow,
        flexDirection: 'row',
        overflow: "hidden",
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'stretch',
      }}
    >
      {({ hovered }) => (
        <View style={{ flex: 1, width: '100%' }}>
          {/* Animated gradient background */}
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}

          {/* Foreground content */}
          <View
            style={{
              paddingHorizontal: '10%',
              flex: 1,
              flexDirection: 'row',
              backgroundColor: hovered ? buttonColors.hover : 'rgba(90,192,234,0)',
              borderRadius: 10,
            }}
          >
            <View style={[styles.centerEverything, { flex: 1, maxWidth: 40 }]}>
              <GoogleIcon />
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: Platform.OS === 'web' ? 0 : 220,
              }}
            >
              <Text
                adjustsFontSizeToFit
                allowFontScaling={false}
                style={{ fontSize: responsiveFontSize(15), color: buttonColors.text, fontWeight: '600' }}
              >
                {t('Sign in with Google')}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
}

export default GoogleButton;